import { useMemo } from 'react';
import { NJHeading } from '@engie-group/fluid-design-system-react';
import { ratiosForDegrees, stationOf } from './domain';
import { ImpactPanel } from './ImpactPanel';
import { StepSettings } from './StepSettings';
import { derive, isPending, type SettingsDraft } from './configDraft';
import { territoryClimate, territoryRatioDefaults } from './territoryClimate';
import type { Perimeter, PlanBuilding, Referential } from './types';

interface Props {
  perimeter: Perimeter;
  buildings: PlanBuilding[];
  referential: Referential;
  draft: SettingsDraft;
  onDraftChange: (patch: Partial<SettingsDraft>) => void;
  onValidity: (ok: boolean) => void;
  onEditingChange: (editing: boolean) => void;
}

/** Onglet « ratios de consommations » : l'étape 2 de l'assistant de création, puis l'impact. */
export function SettingsRatiosTab({ perimeter, buildings, referential, draft, onDraftChange, onValidity, onEditingChange }: Props) {
  const climate = useMemo(() => territoryClimate(perimeter, buildings, referential), [perimeter, buildings, referential]);
  const defaults = useMemo(() => territoryRatioDefaults(climate.zone), [climate.zone]);
  const station = useMemo(() => stationOf({ stationId: draft.dju.stationId, reference: 0 }), [draft.dju.stationId]);
  const stationRatios = useMemo(() => ratiosForDegrees(draft.stationDegrees.dju, draft.stationDegrees.djclim), [draft.stationDegrees]);
  /** Les résultats restent attachés au dernier instantané appliqué, même après édition du climat. */
  const update = (patch: Partial<SettingsDraft>) => {
    const next = derive({ ...draft, ...patch });
    onDraftChange({ ...patch, ratios: next.ratios, dju: next.dju });
  };

  return (
    <StepSettings
      provenance={climate.explanation}
      referential={referential}
      defaultRatios={defaults.ratios}
      station={station}
      stationDegrees={draft.stationDegrees}
      onStationDegreesChange={(stationDegrees) => update({ stationDegrees })}
      myDegrees={draft.myDegrees}
      myRatios={draft.myRatios}
      appliedDegrees={draft.applied.degrees}
      resultDegrees={draft.applied.stationDegrees}
      defaultDegrees={defaults.degrees}
      onMyDegreesChange={(myDegrees) => update({ myDegrees })}
      onMyRatiosChange={(myRatios) => update({ myRatios })}
      projectRatios={draft.ratios}
      stationRatios={stationRatios}
      pending={isPending(draft)}
      applied
      onApply={() => update({ applied: { degrees: { ...draft.myDegrees }, ratios: draft.myRatios, stationDegrees: { ...draft.stationDegrees } } })}
      onRatiosValidity={onValidity}
      onEditingChange={onEditingChange}
      actions={(
        <section className="pnv2-config-section" aria-label="Impact sur les consommations">
          <NJHeading as="h2" scale="sm">Impact sur les consommations</NJHeading>
          <ImpactPanel layout="row" buildings={buildings} ratios={draft.ratios}
            dju={draft.dju} defaultRatios={referential.defaultRatios} defaultDju={referential.defaultDju} />
        </section>
      )}
    />
  );
}
